import { AddOnChargeSource } from '@treadinc/horizon-api-spec';
import { countBy, isNil } from 'lodash-es';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { SettlementInvoiceLines } from '~hooks/useSettlementLines/models';
import { Nullable } from '~types/Nullable';

import {
  isFreightLineItem,
  SettlementSheetCharge,
  SettlementSheetOrderGroup,
} from './settlementSheetLines';

const NO_ORDER_KEY = 'no-order';

const jobStartTime = (invoice: Invoice) => invoice.job?.jobStartAt.valueOf() ?? 0;

const loadOrdinality = (charge: SettlementSheetCharge) =>
  charge.load?.ordinality ?? Number.MAX_SAFE_INTEGER;

const compareCharges = (first: SettlementSheetCharge, second: SettlementSheetCharge) =>
  jobStartTime(first.invoice) - jobStartTime(second.invoice) ||
  (first.invoice.job?.jobId ?? '').localeCompare(second.invoice.job?.jobId ?? '') ||
  loadOrdinality(first) - loadOrdinality(second);

const compareLineItems = (first: InvoiceLineItem, second: InvoiceLineItem) =>
  Number(isFreightLineItem(second)) - Number(isFreightLineItem(first));

export function groupSettlementSheetLines(invoiceLines: SettlementInvoiceLines[]) {
  const invoiceByLineId = new Map<string, Invoice>();
  const orderGroupsByKey = new Map<string, SettlementSheetOrderGroup>();

  invoiceLines.forEach(({ addOnCharges, invoice, lineItems }) => {
    const orderKey = invoice.order?.id ?? NO_ORDER_KEY;
    const orderGroup = orderGroupsByKey.get(orderKey) ?? {
      charges: [],
      invoice,
      jobAddOnCharges: [],
      key: orderKey,
    };
    const chargesByKey = new Map<string, SettlementSheetCharge>();

    lineItems.forEach((lineItem) => {
      invoiceByLineId.set(lineItem.id, invoice);
      const chargeKey = lineItem.load?.id ?? `job-${invoice.id}`;
      const charge: SettlementSheetCharge = chargesByKey.get(chargeKey) ?? {
        addOnCharges: [],
        invoice,
        key: chargeKey,
        lineItems: [],
        load: lineItem.load ?? null,
      };

      charge.lineItems.push(lineItem);
      chargesByKey.set(chargeKey, charge);
    });

    addOnCharges.forEach((addOnCharge) => {
      if (addOnCharge.id) {
        invoiceByLineId.set(addOnCharge.id, invoice);
      }

      if (!addOnCharge.loadId) {
        orderGroup.jobAddOnCharges.push(addOnCharge);

        return;
      }

      const charge: SettlementSheetCharge = chargesByKey.get(addOnCharge.loadId) ?? {
        addOnCharges: [],
        invoice,
        key: addOnCharge.loadId,
        lineItems: [],
        load: null,
      };

      charge.addOnCharges.push(addOnCharge);
      chargesByKey.set(addOnCharge.loadId, charge);
    });

    orderGroup.charges.push(...chargesByKey.values());
    orderGroupsByKey.set(orderKey, orderGroup);
  });

  const orderGroups = [...orderGroupsByKey.values()].map((orderGroup) => ({
    ...orderGroup,
    charges: [...orderGroup.charges].sort(compareCharges).map((charge) => ({
      ...charge,
      lineItems: [...charge.lineItems].sort(compareLineItems),
    })),
  }));

  return {
    invoiceByLineId,
    orderGroups: orderGroups.sort(
      (first, second) =>
        jobStartTime(first.charges[0]?.invoice ?? first.invoice) -
        jobStartTime(second.charges[0]?.invoice ?? second.invoice),
    ),
  };
}

const addOnChargeMatchKey = (
  jobId: Nullable<string>,
  { addOnId, loadId, name }: AddOnCharge,
) => `${jobId}-${loadId}-${addOnId}-${name}`;

export function withoutInvoiceAddOnChargeCopies(
  settlementAddOnCharges: AddOnCharge[],
  invoiceLines: SettlementInvoiceLines[],
) {
  const shownInvoiceAddOnChargeIds = new Set(
    invoiceLines.flatMap(({ addOnCharges }) =>
      addOnCharges.flatMap(({ id }) => (id ? [id] : [])),
    ),
  );
  const invoiceAddOnChargeKeys = invoiceLines.flatMap(({ addOnCharges, invoice }) =>
    addOnCharges.map((addOnCharge) =>
      addOnChargeMatchKey(addOnCharge.job?.id ?? invoice.job?.id, addOnCharge),
    ),
  );
  const unclaimedInvoiceAddOnChargeCounts = new Map(
    Object.entries(countBy(invoiceAddOnChargeKeys)),
  );

  const copiesShownInvoiceAddOnCharge = ({ source, sourceId }: AddOnCharge) =>
    source === AddOnChargeSource.INVOICE &&
    !isNil(sourceId) &&
    shownInvoiceAddOnChargeIds.has(sourceId);

  const isCopyWithoutSourceId = ({ source, sourceId }: AddOnCharge) =>
    source === AddOnChargeSource.INVOICE && isNil(sourceId);

  const claimShownInvoiceAddOnChargeByKey = (addOnCharge: AddOnCharge) => {
    if (!isCopyWithoutSourceId(addOnCharge) || isNil(addOnCharge.job)) {
      return false;
    }

    const key = addOnChargeMatchKey(addOnCharge.job.id, addOnCharge);
    const unclaimedCount = unclaimedInvoiceAddOnChargeCounts.get(key) ?? 0;

    if (unclaimedCount === 0) {
      return false;
    }

    unclaimedInvoiceAddOnChargeCounts.set(key, unclaimedCount - 1);

    return true;
  };

  return settlementAddOnCharges.filter(
    (addOnCharge) =>
      !copiesShownInvoiceAddOnCharge(addOnCharge) &&
      !claimShownInvoiceAddOnChargeByKey(addOnCharge),
  );
}
